import { Injectable } from "@angular/core";
import * as _ from "lodash";
import { Subject } from "rxjs";
import { take } from "rxjs/operators";
import { GeneralDocumentsDocuments, DocumentTreeNode } from "../services/service/DataProviders/GeneralDocumentsDocuments";

export interface SystemStatusOption {
  state: string;
  name: string;
}

@Injectable({
  providedIn: "root",
})
export class ChangeStatus {
  public log$ = new Subject();

  // системните статуси са фиксирани, потребителските се вземат от DocumentType на всеки документ
  public stateOptions: SystemStatusOption[] = [
    { state: "New", name: "Нов" },
    { state: "Planned", name: "Планиран" },
    { state: "FirmPlanned", name: "Потвърден" },
    { state: "Released", name: "Пуснат" },
  ];

  private defaultDelay = 200;

  constructor(private documentsService: GeneralDocumentsDocuments) {}

  subscribeForLog() {
    return this.log$.asObservable();
  }

  public getSystemStatusName(state: string): string {
    const found = _.find(this.stateOptions, (o) => o.state === state);
    return found ? found.name : state;
  }

  // ако документът вече е в статус "Пуснат" не може да се връща назад към По-нисък статус - позволяваме само "Пуснат"
  public getSystemStatusOptions(node: DocumentTreeNode): SystemStatusOption[] {
    if (_.get(node.document, "State") === "Released") {
      return _.filter(this.stateOptions, (o) => o.state === "Released");
    }
    return this.stateOptions;
  }

  public getUserStatusOptions(node: DocumentTreeNode, state: string): any[] {
    if (!state) {
      return [];
    }

    const statuses = _.get(node.document, "DocumentType.UserStatuses", []);
    return _.sortBy(_.filter(statuses, (s) => s.State === state), "DisplayOrder");
  }

  private findMatchingUserStatus(node: DocumentTreeNode, state: string, userStatusName: string): any | null {
    const statuses = _.get(node.document, "DocumentType.UserStatuses", []);
    return _.find(statuses, (s) => s.State === state && _.get(s, "UserStatusName.BG") === userStatusName) || null;
  }

  // прилага избрания системен + потребителски статус на документа и каскадно на всички документи под него,
  // презаписвайки всяко предходно зададено искане за смяна
  public applyStatusCascade(node: DocumentTreeNode, state: string, userStatusName: string) {
    this.applyToNode(node, state, userStatusName);
    this.cascadeToChildren(node.children, state, userStatusName);
  }

  private cascadeToChildren(nodes: DocumentTreeNode[], state: string, userStatusName: string) {
    for (const child of nodes) {
      this.applyToChildNode(child, state, userStatusName);
      this.cascadeToChildren(child.children, state, userStatusName);
    }
  }

  private applyToNode(node: DocumentTreeNode, state: string, userStatusName: string) {
    node.document._pendingState = state;
    node.document._pendingUserStatusName = userStatusName;
    node.document._skippedLowerOrder = false;

    // документ в статус "Пуснат" не може да се връща в по-нисък статус - такъв документ е невалиден за тази смяна
    const isDowngradeFromReleased = _.get(node.document, "State") === "Released" && state !== "Released";
    if (isDowngradeFromReleased) {
      node.document._pendingUserStatus = null;
      node.document._invalid = true;
      node.document._invalidReason = "Документът е в статус \"Пуснат\" и не може да се връща в по-нисък статус";
      return;
    }

    const match = this.findMatchingUserStatus(node, state, userStatusName);
    node.document._pendingUserStatus = match;
    node.document._invalid = !match;
    node.document._invalidReason = match ? null : "Няма съответстващ потребителски статус за този документ";
  }

  // прилага автоматично каскадния статус към дете - ако новият потребителски статус е с по-нисък ред (DisplayOrder)
  // от текущия на детето, не задаваме искане за смяна, а само го маркираме за визуално предупреждение
  private applyToChildNode(node: DocumentTreeNode, state: string, userStatusName: string) {
    node.document._skippedLowerOrder = false;

    const isDowngradeFromReleased = _.get(node.document, "State") === "Released" && state !== "Released";
    if (isDowngradeFromReleased) {
      node.document._pendingState = state;
      node.document._pendingUserStatusName = userStatusName;
      node.document._pendingUserStatus = null;
      node.document._invalid = true;
      node.document._invalidReason = "Документът е в статус \"Пуснат\" и не може да се връща в по-нисък статус";
      return;
    }

    const match = this.findMatchingUserStatus(node, state, userStatusName);
    if (!match) {
      node.document._pendingState = state;
      node.document._pendingUserStatusName = userStatusName;
      node.document._pendingUserStatus = null;
      node.document._invalid = true;
      node.document._invalidReason = "Няма съответстващ потребителски статус за този документ";
      return;
    }

    const currentOrder = _.get(node.document, "UserStatus.DisplayOrder");
    if (!_.isNil(currentOrder) && match.DisplayOrder < currentOrder) {
      node.document._pendingState = null;
      node.document._pendingUserStatusName = null;
      node.document._pendingUserStatus = null;
      node.document._invalid = false;
      node.document._invalidReason = null;
      node.document._skippedLowerOrder = true;
      return;
    }

    node.document._pendingState = state;
    node.document._pendingUserStatusName = userStatusName;
    node.document._pendingUserStatus = match;
    node.document._invalid = false;
    node.document._invalidReason = null;
  }

  public clearPendingStatus(node: DocumentTreeNode) {
    node.document._pendingState = null;
    node.document._pendingUserStatusName = null;
    node.document._pendingUserStatus = null;
    node.document._invalid = false;
    node.document._invalidReason = null;
    node.document._skippedLowerOrder = false;
  }

  public hasPendingChanges(node: DocumentTreeNode): boolean {
    if (node.document._pendingState) {
      return true;
    }
    return _.some(node.children, (c) => this.hasPendingChanges(c));
  }

  public hasInvalidNodes(node: DocumentTreeNode): boolean {
    if (node.document._invalid) {
      return true;
    }
    return _.some(node.children, (c) => this.hasInvalidNodes(c));
  }

  public canProcess(root: DocumentTreeNode | null): boolean {
    if (!root) {
      return false;
    }
    return this.hasPendingChanges(root) && !this.hasInvalidNodes(root);
  }

  // обработва дървото от родители към деца (DFS, родителят винаги се обработва преди децата си)
  public async processTree(root: DocumentTreeNode) {
    await this.processNode(root);
  }

  private async processNode(node: DocumentTreeNode) {
    const doc = node.document;

    if (doc._pendingState) {
      await this.delay(this.defaultDelay);

      const documentId = this.documentsService.resolveDocumentId(doc);
      const documentNo = _.get(doc, "DocumentNo", documentId);
      const data: any = { State: doc._pendingState };

      const fromLabel = this.getSystemStatusName(doc.State) + "/" + _.get(doc, "UserStatus.UserStatusName.BG", "-");
      const toLabel = this.getSystemStatusName(doc._pendingState) + "/" + (doc._pendingUserStatusName || "-");

      if (doc._pendingUserStatus) {
        data.UserState = { "@odata.id": doc._pendingUserStatus["@odata.id"] };
      }

      try {
        await this.documentsService.changeDocumentState(documentId, data, false).pipe(take(1)).toPromise();

        doc.State = doc._pendingState;
        doc.UserStatus = doc._pendingUserStatus;
        this.clearPendingStatus(node);

        this.log$.next({
          message: documentNo + ": " + fromLabel + " -> " + toLabel,
          type: "SUCCESS",
        });
      } catch (e) {
        let err = _.get(e, "error.error.message", "");
        if (err.length == 0) {
          err = _.get(e, "error", "");
        }
        this.log$.next({
          message: documentNo + ": " + fromLabel + " -> " + toLabel + " - " + err,
          type: "ERR",
        });
      }
    }

    for (const child of node.children) {
      await this.processNode(child);
    }
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
